import React, { createContext, useCallback, useState } from 'react';

import { ProductEntity } from '../Product.interface';
import { ProductBoardColumnEntity } from './ProductBoardColumn';

export interface ProductBoardContextProps {
  selected?: ProductEntity | Partial<ProductEntity>;
  entity?: ProductBoardColumnEntity;
  setSelected: (
    productEntity: ProductEntity | Partial<ProductEntity>,
    entity: ProductBoardColumnEntity
  ) => void;
  toggleSelected: (
    productEntity: ProductEntity | Partial<ProductEntity>,
    entity: ProductBoardColumnEntity
  ) => void;
  removeSelected: () => void;
  isSelected: (
    id: number | undefined,
    entity: ProductBoardColumnEntity
  ) => boolean;
}

export const ProductBoardContext = createContext<ProductBoardContextProps>({
  setSelected: () => {},
  toggleSelected: () => {},
  removeSelected: () => {},
  isSelected: () => false
});

export const ProductBoardProvider: React.FC = ({ children }) => {
  const [selected, setSelected] = useState<
    ProductEntity | Partial<ProductEntity> | undefined
  >();
  const [entity, setEntity] = useState<ProductBoardColumnEntity | undefined>();

  const isSelected = useCallback(
    (
      id: number | undefined,
      entityValue: ProductBoardColumnEntity
    ): boolean => {
      return entityValue === entity && !!selected && selected.id === id;
    },
    [entity, selected]
  );

  const removeSelected = useCallback(() => {
    setEntity(undefined);
    setSelected(undefined);
  }, []);

  const setSelectedEntity = useCallback(
    (
      productEntity: ProductEntity | Partial<ProductEntity>,
      entity: ProductBoardColumnEntity
    ) => {
      setEntity(entity);
      setSelected(productEntity);
    },
    []
  );

  const toggleSelected = useCallback(
    (
      productEntity: ProductEntity | Partial<ProductEntity>,
      entity: ProductBoardColumnEntity
    ) => {
      if (isSelected(productEntity.id, entity)) {
        removeSelected();
      } else {
        setSelectedEntity(productEntity, entity);
      }
    },
    [isSelected, removeSelected, setSelectedEntity]
  );

  return (
    <ProductBoardContext.Provider
      value={{
        selected,
        entity,
        isSelected,
        removeSelected,
        toggleSelected,
        setSelected: setSelectedEntity
      }}
    >
      {children}
    </ProductBoardContext.Provider>
  );
};
